import { useState, useEffect } from "react";
import { useAdmin } from "@/contexts/AdminContext";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function AdminSettings() {
  const { api } = useAdmin();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (!settings) return <p className="text-slate-400">Loading...</p>;

  const Field = ({ label, field, textarea }) => (
    <div>
      <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">{label}</label>
      {textarea ? (
        <textarea value={settings[field] || ""} onChange={e => setSettings(s => ({ ...s, [field]: e.target.value }))} rows={3} className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px] focus:border-[#0A1628] focus:outline-none" />
      ) : (
        <input type="text" value={settings[field] || ""} onChange={e => setSettings(s => ({ ...s, [field]: e.target.value }))} className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px] focus:border-[#0A1628] focus:outline-none" />
      )}
    </div>
  );

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-2xl text-[#0A1628] font-medium">Global Settings</h1>
        <button onClick={handleSave} disabled={saving} data-testid="save-settings" className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-5 py-2.5 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px] disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving..." : "Save"}
        </button>
      </div>
      <div className="bg-white border border-slate-200 p-6 rounded-[2px] space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Firm Name" field="firm_name" />
          <Field label="Tagline" field="tagline" />
        </div>
        <Field label="Description" field="description" textarea />
        <Field label="Logo URL" field="logo_url" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Primary Email" field="primary_email" />
          <Field label="Primary Phone" field="primary_phone" />
          <Field label="LinkedIn URL" field="linkedin_url" />
          <Field label="Twitter URL" field="twitter_url" />
        </div>
        <Field label="Footer Disclaimer" field="disclaimer" textarea />
      </div>
    </div>
  );
}
